import React, { useState, Fragment, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import "fontsource-roboto";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { Typography } from "@material-ui/core";
import { find, get, keys } from "lodash";

import "./misc/index.css";
import "./style.css";
import App from "./App";
import Players from "./Players";
import TopBar from "./TopBar";
import AssetsList from "./AssetsList";
import * as serviceWorker from "./misc/serviceWorker";
import useWindowSize from "./helpers/windowSize";
import isTablet from "./helpers/isTablet";
import { request } from "./requests";

const Index = () => {
  const size = useWindowSize();
  const tablet = isTablet(size);
  const [selectedPlayer, setSelectedPlayer] = useState();
  const [signedIn, setSignedIn] = useState(
    typeof localStorage.getItem("token") !== undefined
      ? Boolean(localStorage.getItem("token"))
      : false
  );

  const setSigninValue = ({ token, name }) => {
    if (token) {
      localStorage.setItem("token", token);
      localStorage.setItem("name", name);
      setSignedIn(true);
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("name");
      setSignedIn(false);
    }
  };

  // AssetsList specific states
  const [assetsStatus, updateAssetsStatus] = useState({});
  const [currentlyPlaying, updateCurrentlyPlaying] = useState({});
  const [startPlayTime, updateStartPlayTime] = useState({});
  const [timeInterval, updateTimeInterval] = useState({});
  const [currentProgress, updateCurrentProgress] = useState({});
  const [durations, updateDurations] = useState({});

  const setAssetsStatus = useCallback(
    (value, player) => {
      updateAssetsStatus({ ...assetsStatus, [player]: value });
    },
    [assetsStatus]
  );

  const setCurrentlyPlaying = useCallback(
    (value, player) => {
      updateCurrentlyPlaying({ ...currentlyPlaying, [player]: value });
    },
    [currentlyPlaying]
  );

  const setStartPlayTime = useCallback(
    (value, player) => {
      updateStartPlayTime({ ...startPlayTime, [player]: value });
    },
    [startPlayTime]
  );

  const setCurrentProgress = useCallback(
    (value, player) => {
      updateCurrentProgress({
        ...currentProgress,
        [player]: parseInt(value),
      });
    },
    [currentProgress]
  );

  const setDurations = useCallback(
    (value, player) => {
      updateDurations({
        ...durations,
        [player]: parseInt(value),
      });
    },
    [durations]
  );

  const setTimeInterval = useCallback(
    (duration, player) => {
      keys(timeInterval).forEach((key) => {
        window.clearInterval(timeInterval[key]);
      });
      if (duration) {
        setDurations(duration, player);
        const interval = window.setInterval(
          (startPlayTime) => {
            setCurrentProgress(
              parseInt(
                ((new Date().getTime() - startPlayTime) / duration) * 100
              ),
              player
            );
          },
          50,
          startPlayTime[player]
        );
        updateTimeInterval({ [player]: interval });
      } else {
        updateTimeInterval({ [player]: undefined });
      }
    },
    [timeInterval, setCurrentProgress, startPlayTime, setDurations]
  );

  const resetPlay = useCallback(
    (player) => {
      window.clearInterval(timeInterval[selectedPlayer]);
      setCurrentlyPlaying(undefined, player);
      setAssetsStatus({}, player);
      setTimeInterval(undefined, player);
      setCurrentProgress(0, player);
    },
    [
      timeInterval,
      setTimeInterval,
      setAssetsStatus,
      setCurrentProgress,
      setCurrentlyPlaying,
      selectedPlayer,
    ]
  );

  // Handle toggling play stop
  const togglePlay = (id, assets) => {
    const asset = find(assets, ({ _id }) => _id === id);
    const index = get(asset, "program.trigger_index");

    // Media is already playing, this is a toggle
    const configs = {
      method: "post",
      type: "trigger",
      value: { device_id: selectedPlayer },
    };

    if (get(assetsStatus[selectedPlayer], `${id}`)) {
      resetPlay(selectedPlayer);
      request({
        ...configs,
        data: {
          action: "stop",
          index,
          layout: "fullscreen",
        },
      });
    } else {
      resetPlay(selectedPlayer);
      // Stop all previously playing medias just to make sure we are ready
      request({
        ...configs,
        data: {
          action: "stop-all",
          layout: "fullscreen",
        },
      }).then(() => {
        request({
          ...configs,
          data: {
            action: "start",
            index,
            layout: "fullscreen",
          },
        }).then(() => {
          setAssetsStatus(
            {
              [id]: !get(assetsStatus[selectedPlayer], `${id}`),
            },
            selectedPlayer
          );
          setStartPlayTime(new Date().getTime(), selectedPlayer);
          setCurrentlyPlaying(id, selectedPlayer);
        });
      });
    }
  };

  // Reset playing status once the video is done
  useEffect(() => {
    keys(currentProgress).forEach((key) => {
      if (currentlyPlaying[key]) {
        if (currentProgress[key] >= 100) {
          resetPlay(key);
        }
      }
    });
  }, [currentlyPlaying, currentProgress, resetPlay]);

  const assetsProps = {
    assetsStatus: assetsStatus[selectedPlayer],
    currentlyPlaying: currentlyPlaying[selectedPlayer],
    startPlayTime: startPlayTime[selectedPlayer],
    timeInterval: timeInterval[selectedPlayer],
    currentProgress: currentProgress[selectedPlayer],
    setAssetsStatus,
    setCurrentlyPlaying,
    setTimeInterval,
    setCurrentProgress,
    resetPlay,
    togglePlay,
  };

  return (
    <React.StrictMode>
      <div id="Main">
        <Router>
          <Switch>
            <Route exact path="/">
              <App setSignedIn={setSigninValue} signedIn={signedIn} />
            </Route>
            {!tablet && (
              <Fragment>
                <Route path="/players">
                  {signedIn === true && <TopBar setSignedIn={setSigninValue} />}
                  <Players
                    setSelectedPlayer={setSelectedPlayer}
                    selectedPlayer={selectedPlayer}
                  />
                </Route>
                <Route path="/assets">
                  {signedIn === true && (
                    <TopBar
                      setSignedIn={setSigninValue}
                      displayBackButton={true}
                    />
                  )}
                  <AssetsList {...assetsProps} />
                </Route>
              </Fragment>
            )}
            {tablet && (
              <Route path={["/players", "/assets"]}>
                {signedIn === true && <TopBar setSignedIn={setSigninValue} />}
                <div className="PlayerAssetContainer">
                  <Players
                    setSelectedPlayer={setSelectedPlayer}
                    selectedPlayer={selectedPlayer}
                  />
                  <AssetsList {...assetsProps} />
                </div>
              </Route>
            )}
          </Switch>
        </Router>
        {signedIn && (
          <div className="Footer">
            <Typography variant="caption">
              Logged in as {localStorage.getItem("name")}
            </Typography>
          </div>
        )}
      </div>
    </React.StrictMode>
  );
};

ReactDOM.render(<Index />, document.getElementById("root"));

serviceWorker.unregister();
